import React, { useMemo } from "react";
import {
  Briefcase, FileText, FolderGit2, Trophy, BadgeCheck, Youtube,
  Check, ArrowRight, Sparkles,
} from "lucide-react";
import { AppCard, AppCardContent, PanelButton } from "../ui";
import ScoreRing from "./ScoreRing";
import { scoreBand, TONE_CLASSES } from "./constants";

/**
 * PortfolioStrength — how complete the portfolio is, and what to do next.
 *
 * ⚠️ COMPLETENESS IS NOT THE RESUME SCORE, and the card keeps them visibly
 * apart. Completeness is arithmetic this component does — did you fill in the
 * section — and it is honest because it measures presence, not quality. The
 * resume score is a language model's judgement of a document. Blending them
 * into one number would let "added three empty-ish projects" raise a figure that
 * reads as "your resume got better".
 *
 * The checklist is ordered by what actually moves a student forward: a target
 * role first, because without one the resume analysis can only check
 * formatting; then a resume to analyse; then the evidence.
 */

/**
 * Weights, and why they are not equal.
 *
 * The career profile and the resume are the two things this module exists to
 * produce, so they carry half the total between them. Projects are the strongest
 * evidence a student can offer and carry the largest single share of the rest.
 * Videos are genuinely optional and are weighted like it — a student with none
 * is not incomplete, and a scale that told them otherwise would be lying to
 * push a feature.
 */
const CHECKS = [
  {
    key: "role", label: "Career profile", weight: 20, icon: Briefcase, section: "portfolio-profile",
    hint: "Name the role you are applying for",
    done: (p) => !!p?.careerProfile?.targetRole,
  },
  {
    key: "skills", label: "Skills listed", weight: 15, icon: Briefcase, section: "portfolio-profile",
    hint: "Add at least three skills",
    done: (p) => (p?.careerProfile?.skills?.length || 0) >= 3,
  },
  {
    key: "resume", label: "Resume uploaded", weight: 20, icon: FileText, section: "portfolio-resume",
    hint: "Upload a PDF of your CV",
    done: (p) => !!p?.resume?.key,
  },
  {
    key: "analysis", label: "Resume analysed", weight: 10, icon: Sparkles, section: "portfolio-resume",
    hint: "Run the AI analysis to see what to fix",
    done: (p, a) => a?.status === "completed",
  },
  {
    key: "projects", label: "Projects added", weight: 20, icon: FolderGit2, section: "portfolio-projects",
    hint: "Add at least two projects with links",
    done: (p) => (p?.projects?.length || 0) >= 2,
  },
  {
    key: "credentials", label: "Certifications", weight: 10, icon: BadgeCheck, section: "portfolio-certifications",
    hint: "Add a certification, or earn one here",
    done: (p, a, earned) => (p?.certifications?.length || 0) > 0 || (earned?.certificates?.length || 0) > 0,
  },
  {
    key: "achievements", label: "Achievements", weight: 3, icon: Trophy, section: "portfolio-achievements",
    hint: "Awards, hackathons, publications",
    done: (p) => (p?.achievements?.length || 0) > 0,
  },
  {
    key: "videos", label: "Showcase video", weight: 2, icon: Youtube, section: "portfolio-videos",
    hint: "A short demo of one project",
    done: (p) => (p?.showcaseVideos?.length || 0) > 0,
  },
];

const PortfolioStrength = ({ portfolio, latestAnalysis, earned, onJump }) => {
  const { score, next, doneCount } = useMemo(() => {
    let earnedPoints = 0;
    const pending = [];
    CHECKS.forEach((c) => {
      if (c.done(portfolio, latestAnalysis, earned)) earnedPoints += c.weight;
      else pending.push(c);
    });
    const total = CHECKS.reduce((s, c) => s + c.weight, 0);
    return {
      score: Math.round((earnedPoints / total) * 100),
      next: pending.slice(0, 3),
      doneCount: CHECKS.length - pending.length,
    };
  }, [portfolio, latestAnalysis, earned]);

  const band = scoreBand(score);
  const tone = TONE_CLASSES[band.tone];
  const complete = next.length === 0;

  return (
    <AppCard>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-5 min-[720px]:tw-flex-row min-[720px]:tw-items-center">
        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-4">
          <ScoreRing value={score} size={104} thickness={9} label="Complete" showBand={false} />
          <div className="min-[720px]:tw-hidden">
            <h3 className="tw-m-0 tw-text-base tw-font-semibold tw-text-content">Your portfolio</h3>
            <p className={`tw-m-0 tw-text-sm tw-font-medium ${tone.text}`}>
              {doneCount} of {CHECKS.length} steps done
            </p>
          </div>
        </div>

        <div className="tw-min-w-0 tw-flex-1">
          <div className="tw-hidden min-[720px]:tw-block">
            <h3 className="tw-m-0 tw-text-base tw-font-semibold tw-text-content">Your portfolio</h3>
            <p className="tw-m-0 tw-mt-0.5 tw-text-[0.85rem] tw-leading-relaxed tw-text-content-muted">
              {complete
                ? "Everything is filled in. Keep it current as you build more."
                : `${doneCount} of ${CHECKS.length} steps done. Your institute's staff and your instructors can see this.`}
            </p>
          </div>

          {!complete && (
            <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-1.5">
              <span className="tw-text-[0.7rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">
                Do next
              </span>
              <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
                {next.map(({ key, label, hint, icon: Icon, section }) => (
                  <li key={key}>
                    <PanelButton className="tw-group tw-gap-2.5" onClick={() => onJump?.(section)}>
                      <Icon size={15} className="tw-shrink-0 tw-text-content-subtle group-hover:tw-text-primary" />
                      <span className="tw-min-w-0 tw-flex-1">
                        <span className="tw-block tw-text-[0.82rem] tw-font-medium tw-text-content">{label}</span>
                        <span className="tw-block tw-text-xs tw-text-content-subtle">{hint}</span>
                      </span>
                      <ArrowRight size={14} className="tw-shrink-0 tw-text-content-subtle group-hover:tw-text-primary" />
                    </PanelButton>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {complete && (
            <p className="tw-m-0 tw-mt-3 tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-success">
              <Check size={15} /> Every section is filled in.
            </p>
          )}
        </div>
      </AppCardContent>
    </AppCard>
  );
};

export default PortfolioStrength;
